import PropTypes from "prop-types";

/**
 * FOD by DMB Partners composite logo.
 * Inlined SVGs = no network request + reliable color control.
 * Variants:
 *   - "dark"  : for use on light backgrounds (default) — FOD navy, DMB green mark
 *   - "light" : for use on dark backgrounds — FOD white, DMB green mark + white Partners
 */
export function Logo({ variant = "dark", fodWidth = 88, className = "" }) {
    const fodFill = variant === "light" ? "#FFFFFF" : "#0F1B2D";
    const partnersFill = variant === "light" ? "#FFFFFF" : "#0F1B2D";
    const byColor = variant === "light" ? "#CDC8BF" : "#0F1B2D";
    const dividerBg = variant === "light" ? "rgba(255,255,255,0.3)" : "#D6CBB6";

    const fodHeight = Math.round((fodWidth * 631) / 1855); // keep aspect ratio
    const dmbWidth = Math.round((fodWidth * 113) / 88);
    const dmbHeight = Math.round((dmbWidth * 12) / 113);
    const maskId = `dmb-mask-${variant}-${fodWidth}`;

    return (
        <span
            data-testid="brand-logo"
            className={`inline-flex items-center gap-3 ${className}`}
            aria-label="FOD by DMB Partners"
        >
            {/* FOD letters */}
            <svg
                viewBox="361 361 1855 631"
                width={fodWidth}
                height={fodHeight}
                aria-hidden="true"
            >
                <g fill={fodFill}>
                    <circle cx="451" cy="451" r="90"></circle>
                    <rect x="722" y="361" width="180" height="181"></rect>
                    <path d="M361 632 H721 V811 H541 V992 H361 Z"></path>
                    <path
                        fillRule="evenodd"
                        d="M1279 361 A316 316 0 1 1 1278.9 361 Z M1279 581 A95 95 0 1 0 1279.1 581 Z"
                    ></path>
                    <rect x="1675" y="361" width="182" height="416"></rect>
                    <rect x="1675" y="811" width="181" height="181"></rect>
                    <path d="M1856 361 H1900 A315.5 315.5 0 0 1 1900 992 H1889 V811 H1900 A134.5 134.5 0 0 0 1900 542 H1856 Z"></path>
                </g>
            </svg>

            {/* Vertical divider */}
            <span
                aria-hidden="true"
                style={{ width: 1, height: 26, background: dividerBg }}
            />

            {/* by DMB Partners */}
            <span className="inline-flex items-center gap-1.5">
                <span style={{ fontSize: 12, color: byColor }}>by</span>
                <svg
                    viewBox="100 100 1800 192"
                    width={dmbWidth}
                    height={dmbHeight}
                    aria-hidden="true"
                >
                    <defs>
                        <mask id={maskId} maskUnits="userSpaceOnUse" x="95" y="95" width="180" height="200">
                            <rect x="95" y="95" width="180" height="200" fill="#FFFFFF"></rect>
                            <path d="M157 157 H168 A39 39 0 0 1 168 235 H157 Z" fill="#000000"></path>
                            <rect x="157" y="157" width="9" height="135" fill="#000000"></rect>
                            <rect x="95" y="225" width="62" height="10" fill="#000000"></rect>
                        </mask>
                    </defs>
                    <path d="M104 105 H168 A91 91 0 0 1 168 287 H104 Z" fill="#4EAE32" mask={`url(#${maskId})`}></path>
                    <text
                        x="283"
                        y="287"
                        fontFamily="Poppins, sans-serif"
                        fontWeight="700"
                        fontSize="260"
                        textLength="395"
                        lengthAdjust="spacingAndGlyphs"
                        fill="#4EAE32"
                    >
                        MB
                    </text>
                    <text
                        x="750"
                        y="287"
                        fontFamily="Poppins, sans-serif"
                        fontWeight="700"
                        fontSize="260"
                        textLength="1146"
                        lengthAdjust="spacingAndGlyphs"
                        fill={partnersFill}
                    >
                        Partners
                    </text>
                </svg>
            </span>
        </span>
    );
}

Logo.propTypes = {
    variant: PropTypes.oneOf(["dark", "light"]),
    fodWidth: PropTypes.number,
    className: PropTypes.string,
};

/**
 * Stacked variant used only in footer: FOD on top, filet, "by DMB Partners" below.
 */
export function LogoStacked({ fodWidth = 132, className = "" }) {
    const fodHeight = Math.round((fodWidth * 631) / 1855);

    return (
        <span
            data-testid="brand-logo-stacked"
            className={`inline-flex flex-col items-start gap-2.5 ${className}`}
            aria-label="FOD by DMB Partners"
        >
            <svg viewBox="361 361 1855 631" width={fodWidth} height={fodHeight} aria-hidden="true">
                <g fill="#FFFFFF">
                    <circle cx="451" cy="451" r="90"></circle>
                    <rect x="722" y="361" width="180" height="181"></rect>
                    <path d="M361 632 H721 V811 H541 V992 H361 Z"></path>
                    <path fillRule="evenodd" d="M1279 361 A316 316 0 1 1 1278.9 361 Z M1279 581 A95 95 0 1 0 1279.1 581 Z"></path>
                    <rect x="1675" y="361" width="182" height="416"></rect>
                    <rect x="1675" y="811" width="181" height="181"></rect>
                    <path d="M1856 361 H1900 A315.5 315.5 0 0 1 1900 992 H1889 V811 H1900 A134.5 134.5 0 0 0 1900 542 H1856 Z"></path>
                </g>
            </svg>
            <span style={{ width: fodWidth, height: 1, background: "rgba(255,255,255,0.3)" }} aria-hidden="true" />
            <span className="inline-flex items-baseline gap-1.5 leading-none" data-testid="brand-logo-stacked-dmb">
                <span className="text-xs text-on-navy-soft">by</span>
                <span className="text-[17px] font-bold tracking-[-0.01em] text-white">
                    <span style={{ color: "#4EAE32" }}>DMB</span> Partners
                </span>
            </span>
        </span>
    );
}

LogoStacked.propTypes = {
    fodWidth: PropTypes.number,
    className: PropTypes.string,
};
